import { motion } from "framer-motion";
import { 
  MessageSquare, 
  Hash, 
  Reply,
  Send,
  AtSign,
  Smile,
  Users,
} from "lucide-react";
import { Container, SectionHeading, SectionSubheading } from "./shared";

function MessagingMockup() {
  const messages = [
    { user: "Sarah", avatar: "S", text: "Just completed the first module! The video quality is amazing 🎉", time: "2m", reactions: ["❤️", "🔥"] },
    { user: "Mike", avatar: "M", text: "Can someone help with the exercise in lesson 3?", time: "5m" },
    { user: "Creator", avatar: "C", text: "Great question Mike! Here's a hint: try breaking down the problem into smaller steps first.", time: "3m", isCreator: true },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden h-full"
    >
      <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-emerald-500" />
        <h4 className="text-sm font-semibold text-foreground">Real-time Messaging</h4>
      </div>
      <p className="text-xs text-muted-foreground px-3 py-2">
        Chat with students in real-time, answer questions, and build relationships.
      </p>
      
      <div className="p-3 space-y-3">
        {messages.map((msg, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.1, duration: 0.3 }}
            viewport={{ once: true }}
            className="flex items-start gap-2"
          >
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
              msg.isCreator ? 'bg-emerald-500 text-white' : 'bg-neutral-200 dark:bg-neutral-700 text-muted-foreground'
            }`}>
              {msg.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-medium ${msg.isCreator ? 'text-emerald-500' : 'text-foreground'}`}>{msg.user}</span>
                {msg.isCreator && <span className="text-[10px] bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 rounded">Creator</span>}
                <span className="text-[10px] text-muted-foreground">{msg.time}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{msg.text}</p>
              {msg.reactions && (
                <div className="flex gap-1 mt-1">
                  {msg.reactions.map((r, ri) => (
                    <span key={ri} className="text-[10px] bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded-full">{r}</span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}
        
        {/* Input */}
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-neutral-200 dark:border-neutral-800">
          <div className="flex-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg px-3 py-2 text-xs text-muted-foreground">
            Type a message...
          </div>
          <button className="p-2 bg-emerald-500 rounded-lg">
            <Send className="w-3 h-3 text-white" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

function ChannelsMockup() {
  const channels = [
    { name: "general", unread: 0, active: true },
    { name: "introductions", unread: 5 },
    { name: "module-1-discussion", unread: 0 },
    { name: "q-and-a", unread: 12 },
    { name: "resources", unread: 0 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden h-full"
    >
      <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-2">
        <Hash className="w-4 h-4 text-emerald-500" />
        <h4 className="text-sm font-semibold text-foreground">Channels & Threads</h4>
      </div>
      <p className="text-xs text-muted-foreground px-3 py-2">
        Organize discussions by topic with dedicated channels and threaded replies.
      </p>
      
      <div className="p-3 space-y-1">
        {channels.map((channel, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.05, duration: 0.3 }}
            viewport={{ once: true }}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer transition-colors ${
              channel.active ? 'bg-emerald-500/10 text-emerald-500' : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-muted-foreground'
            }`}
          >
            <Hash className="w-3.5 h-3.5" />
            <span className="flex-1 text-xs font-medium">{channel.name}</span>
            {channel.unread > 0 && (
              <span className="bg-emerald-500 text-white text-[10px] px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                {channel.unread}
              </span>
            )}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

function ReactionsMockup() {
  const reactions = ["👍", "❤️", "🔥", "🎉", "💡", "😄"];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden h-full"
    >
      <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-2">
        <Smile className="w-4 h-4 text-emerald-500" />
        <h4 className="text-sm font-semibold text-foreground">Reactions & Replies</h4>
      </div>
      <p className="text-xs text-muted-foreground px-3 py-2">
        Engage with emoji reactions and threaded replies for deeper conversations.
      </p>
      
      <div className="p-3">
        {/* Sample message with reactions */}
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
          <div className="flex items-start gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center text-[10px] font-medium text-white">J</div>
            <div className="flex-1">
              <span className="text-xs font-medium text-foreground">John</span>
              <p className="text-xs text-muted-foreground">This lesson was incredibly helpful!</p>
            </div>
          </div>
          
          {/* Reactions */}
          <div className="flex items-center gap-1 ml-8">
            {reactions.slice(0, 4).map((r, i) => (
              <motion.button
                key={i}
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ delay: 0.4 + i * 0.05, type: "spring" }}
                viewport={{ once: true }}
                className="bg-white dark:bg-neutral-700 border border-neutral-200 dark:border-neutral-600 rounded-full px-2 py-1 text-sm hover:scale-110 transition-transform"
              >
                {r} <span className="text-[10px] text-muted-foreground">{Math.floor(Math.random() * 10) + 1}</span>
              </motion.button>
            ))}
            <button className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors">
              <Smile className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>
        
        {/* Reply button */}
        <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-emerald-500 mt-2 ml-8 transition-colors">
          <Reply className="w-3 h-3" />
          Reply in thread
        </button>
      </div>
    </motion.div>
  );
}

function DirectMessagesMockup() {
  const conversations = [
    { name: "Sarah Johnson", avatar: "S", preview: "Thanks for the feedback!", unread: true, time: "2m" },
    { name: "Mike Chen", avatar: "M", preview: "When is the next live session?", time: "1h" },
    { name: "Emma Wilson", avatar: "E", preview: "The course is amazing!", time: "2h" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      viewport={{ once: true }}
      className="bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden h-full"
    >
      <div className="p-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center gap-2">
        <AtSign className="w-4 h-4 text-emerald-500" />
        <h4 className="text-sm font-semibold text-foreground">Direct Messages</h4>
      </div>
      <p className="text-xs text-muted-foreground px-3 py-2">
        Private conversations with students for personalized support.
      </p>
      
      <div className="p-3 space-y-2">
        {conversations.map((conv, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.1, duration: 0.3 }}
            viewport={{ once: true }}
            className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer transition-colors"
          >
            <div className="relative">
              <div className="w-8 h-8 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-xs font-medium text-muted-foreground">
                {conv.avatar}
              </div>
              {conv.unread && (
                <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-neutral-900" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium ${conv.unread ? 'text-foreground' : 'text-muted-foreground'}`}>{conv.name}</span>
                <span className="text-[10px] text-muted-foreground">{conv.time}</span>
              </div>
              <p className="text-[10px] text-muted-foreground truncate">{conv.preview}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export function CommunityFeatures() {
  return (
    <section className="py-16 md:py-24 lg:py-32 bg-neutral-50 dark:bg-neutral-950/50">
      <Container>
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <SectionHeading className="mb-4">
              Community Built Right In
            </SectionHeading>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <SectionSubheading className="mx-auto">
              Every course comes with its own community space. No third-party tools, no extra setup — just connect with your students.
            </SectionSubheading>
          </motion.div>
        </div>

        {/* 2x2 Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border border-neutral-200 dark:border-neutral-800 rounded-2xl overflow-hidden bg-white dark:bg-neutral-900">
          <div className="p-6 border-b md:border-b-0 md:border-r border-neutral-200 dark:border-neutral-800">
            <MessagingMockup />
          </div>
          <div className="p-6 border-b md:border-b-0 border-neutral-200 dark:border-neutral-800">
            <ChannelsMockup />
          </div>
          <div className="p-6 md:border-r border-neutral-200 dark:border-neutral-800">
            <ReactionsMockup />
          </div>
          <div className="p-6">
            <DirectMessagesMockup />
          </div>
        </div>

        {/* Online stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="flex items-center justify-center gap-6 mt-8 text-sm text-muted-foreground"
        >
          <span className="flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            Real-time updates
          </span>
          <span className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Unlimited members
          </span>
          <span className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            No message limits
          </span>
        </motion.div>
      </Container>
    </section>
  );
}

